import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useFocusMode } from '../contexts/FocusModeContext';
import { API_BASE } from '../config';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from './ui/sidebar';
import { Link } from './ui/link';
import { ArrowLeft, ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '../lib/utils';
import { ProjectExplorer } from './ProjectExplorer';
import { ProjectSidebarActions } from './ProjectSidebarActions';
import { UserAvatarMenu } from './UserAvatarMenu';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import { useSidebarActionsFit } from '../hooks/use-sidebar-actions-fit';
import { APP_SIDEBAR_HEADER_CLASSNAME, APP_SIDEBAR_HEADER_STYLE } from '../constants/layout';

interface ProjectStructure {
  title: string;
  mainDocumentIcon: string | null;
  mainDocumentId: string | null;
  ownerId: string;
  sharePermission: 'reader' | 'commenter' | 'editor' | 'admin' | null;
}

interface DocumentItem {
  id: string;
  title: string;
  icon: string | null;
  pinned?: boolean;
  projectId: string | null;
  sharedIntoProject?: boolean;
  ownerEmail?: string | null;
  sharePermission?: 'reader' | 'commenter' | 'editor' | null;
}

export const ProjectSidebar: React.FC = () => {
  const params = useParams<{ projectId?: string; documentId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isPathActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const { token, user } = useAuth();
  const { t } = useLocale();
  const { isFocusMode } = useFocusMode();
  const { canEditProject, canManageSharing, isOwner: isProjectOwnerFromAccess, sharePermission: accessSharePermission } = useProjectAccess();
  const [project, setProject] = useState<ProjectStructure | null>(null);
  const [projectDocuments, setProjectDocuments] = useState<DocumentItem[]>([]);
  const [isSharedDocument, setIsSharedDocument] = useState<boolean>(false);
  const { state, isMobile, openMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const isCollapsed = isMobile ? !openMobile : state === 'collapsed';
  const sidebarContentRef = useRef<HTMLDivElement | null>(null);
  const sidebarFooterRef = useRef<HTMLDivElement | null>(null);
  const [sidebarRoot, setSidebarRoot] = useState<HTMLElement | null>(null);
  const [sidebarHeader, setSidebarHeader] = useState<HTMLElement | null>(null);
  const [sidebarContent, setSidebarContent] = useState<HTMLDivElement | null>(null);
  const [sidebarFooter, setSidebarFooter] = useState<HTMLElement | null>(null);
  const [hasMoreContentBelow, setHasMoreContentBelow] = useState(false);

  const handleMobileNavigation = (): void => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  useEffect(() => {
    const fetchProject = async (): Promise<void> => {
      const projectId = params.projectId;
      if (!token || !projectId || projectId === 'new') {
        setProject(null);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setProject({
            title: data.title || '',
            mainDocumentIcon: data.mainDocumentIcon || null,
            mainDocumentId: data.mainDocumentId || null,
            ownerId: data.ownerId || '',
            sharePermission: data.sharePermission || null,
          });
        } else {
          setProject(null);
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        setProject(null);
      }
    };

    fetchProject();
  }, [token, params.projectId]);

  useEffect(() => {
    const handleProjectTitleUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ projectId: string; title: string }>;
      if (customEvent.detail.projectId === params.projectId && project) {
        setProject({
          ...project,
          title: customEvent.detail.title,
        });
      }
    };

    window.addEventListener('project-title-updated', handleProjectTitleUpdate);

    return () => {
      window.removeEventListener('project-title-updated', handleProjectTitleUpdate);
    };
  }, [params.projectId, project]);

  useEffect(() => {
    const fetchProjectDocuments = async (): Promise<void> => {
      const projectId = params.projectId;
      if (!token || !projectId || projectId === 'new') {
        setProjectDocuments([]);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectId}/documents`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setProjectDocuments(data.documents || []);
        } else {
          setProjectDocuments([]);
        }
      } catch (err) {
        console.error('Error fetching project documents:', err);
        setProjectDocuments([]);
      }
    };

    fetchProjectDocuments();
  }, [token, params.projectId]);

  useEffect(() => {
    const handleDocumentCreated = (event: Event): void => {
      const customEvent = event as CustomEvent<{ projectId?: string }>;
      if (customEvent.detail.projectId !== params.projectId) {
        return;
      }
      const fetchProjectDocuments = async (): Promise<void> => {
        const projectId = params.projectId;
        if (!token || !projectId || projectId === 'new') {
          return;
        }

        try {
          const response = await fetch(`${API_BASE}/api/projects/${projectId}/documents`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setProjectDocuments(data.documents || []);
          }
        } catch (err) {
          console.error('Error fetching project documents:', err);
        }
      };

      void fetchProjectDocuments();
    };

    window.addEventListener('document-created', handleDocumentCreated);
    return () => window.removeEventListener('document-created', handleDocumentCreated);
  }, [token, params.projectId]);

  useEffect(() => {
    const handleDocumentRestored = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string; projectId: string }>;
      if (customEvent.detail.projectId === params.projectId) {
        const fetchProjectDocuments = async (): Promise<void> => {
          const projectId = params.projectId;
          if (!token || !projectId || projectId === 'new') {
            return;
          }

          try {
            const response = await fetch(`${API_BASE}/api/projects/${projectId}/documents`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const data = await response.json();
              setProjectDocuments(data.documents || []);
            }
          } catch (err) {
            console.error('Error fetching project documents:', err);
          }
        };

        fetchProjectDocuments();
      }
    };

    window.addEventListener('document-restored', handleDocumentRestored);

    return () => {
      window.removeEventListener('document-restored', handleDocumentRestored);
    };
  }, [token, params.projectId]);

  useEffect(() => {
    const handleDocumentArchived = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string; projectId?: string }>;
      if (customEvent.detail.projectId && customEvent.detail.projectId !== params.projectId) {
        return;
      }
      setProjectDocuments((prev) => prev.filter((doc) => doc.id !== customEvent.detail.documentId));
    };

    const handleDocumentUnarchived = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string; projectId: string }>;
      if (customEvent.detail.projectId !== params.projectId) {
        return;
      }
      const fetchProjectDocuments = async (): Promise<void> => {
        const projectId = params.projectId;
        if (!token || !projectId || projectId === 'new') {
          return;
        }

        try {
          const response = await fetch(`${API_BASE}/api/projects/${projectId}/documents`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setProjectDocuments(data.documents || []);
          }
        } catch (err) {
          console.error('Error fetching project documents:', err);
        }
      };

      void fetchProjectDocuments();
    };

    window.addEventListener('document-archived', handleDocumentArchived);
    window.addEventListener('document-unarchived', handleDocumentUnarchived);

    return () => {
      window.removeEventListener('document-archived', handleDocumentArchived);
      window.removeEventListener('document-unarchived', handleDocumentUnarchived);
    };
  }, [token, params.projectId]);

  useEffect(() => {
    const checkIfSharedDocument = async (): Promise<void> => {
      const documentId = params.documentId;
      if (!token || !documentId || documentId === 'new' || !user) {
        setIsSharedDocument(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/documents/${documentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const hasSharePermission = data.sharePermission !== null && data.sharePermission !== undefined;
          const isOwner = data.ownerId === user.id;
          const viewingViaSharedIntoProject = Boolean(
            params.projectId
            && data.projectId
            && params.projectId !== data.projectId
          );
          setIsSharedDocument(hasSharePermission && !isOwner && !viewingViaSharedIntoProject);
        } else {
          setIsSharedDocument(false);
        }
      } catch (err) {
        console.error('Error checking document share status:', err);
        setIsSharedDocument(false);
      }
    };

    checkIfSharedDocument();
  }, [token, params.documentId, params.projectId, user]);

  const isProjectOwner = Boolean(project && user && project.ownerId === user.id) || isProjectOwnerFromAccess;
  const projectSharePermission = project?.sharePermission || accessSharePermission;
  const canViewProject = isProjectOwner || projectSharePermission !== null;
  const isSharedProjectViewer = !isProjectOwner && projectSharePermission !== null;

  const fullActionBarItemCount =
    1 +
    (canEditProject ? 2 : 0) +
    (canManageSharing ? 1 : 0);

  const { actionsMode: sidebarActionsMode, needsContentScroll } = useSidebarActionsFit({
    enabled: isCollapsed && !isMobile && Boolean(project && canViewProject),
    sidebar: sidebarRoot,
    header: sidebarHeader,
    content: sidebarContent,
    footer: sidebarFooter,
    fullActionBarItemCount,
    separatorCount: 2,
  });

  useEffect(() => {
    const el = sidebarContentRef.current;
    if (!el) return;

    const updateScrollIndicator = (): void => {
      setHasMoreContentBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
    };

    const resizeObserver = new ResizeObserver(updateScrollIndicator);
    const observeChildren = (): void => {
      resizeObserver.disconnect();
      resizeObserver.observe(el);
      Array.from(el.children).forEach((child) => resizeObserver.observe(child));
    };
    observeChildren();

    const mutationObserver = new MutationObserver(() => {
      observeChildren();
      updateScrollIndicator();
    });
    mutationObserver.observe(el, { childList: true, subtree: true });

    updateScrollIndicator();
    el.addEventListener('scroll', updateScrollIndicator);

    return () => {
      el.removeEventListener('scroll', updateScrollIndicator);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [project, isCollapsed, needsContentScroll, sidebarContent]);

  if (isFocusMode) {
    return null;
  }

  return (
    <Sidebar collapsible="icon" className="bg-sidebar text-sidebar-foreground">
      <SidebarHeader
        ref={(node) => {
          setSidebarHeader(node);
          setSidebarRoot((node?.closest('[data-sidebar="sidebar"]') as HTMLElement | null) ?? null);
        }}
        className={APP_SIDEBAR_HEADER_CLASSNAME}
        style={APP_SIDEBAR_HEADER_STYLE}
      >
        <SidebarMenu className="h-full">
          <SidebarMenuItem className={cn('h-full', isCollapsed && 'flex items-center justify-center')}>
            {isCollapsed ? (
              <SidebarMenuButton
                tooltip={t('toggle_sidebar')}
                className="h-[38px] !justify-center !text-center"
                onClick={toggleSidebar}
                aria-label={t('toggle_sidebar')}
              >
                <PanelLeftOpen className="!h-6 !w-6 text-primary" />
                <span className="sr-only">{t('toggle_sidebar')}</span>
              </SidebarMenuButton>
            ) : (
              <div className="flex items-center gap-1 w-full h-full">
                {isSharedDocument || isSharedProjectViewer ? (
                  <SidebarMenuButton asChild tooltip={t('back_to_shared_documents') || 'Back to shared documents'} className="h-[38px] py-0 flex-1">
                    <Link to="/desk/shared-with-me" onClick={handleMobileNavigation} className="flex items-center w-full h-full py-0">
                      <ArrowLeft className="!h-4 !w-4 text-secondary-foreground" />
                      <span className="text-primary">{t('back_to_shared') || t('back_to_shared_documents') || 'Back to shared'}</span>
                    </Link>
                  </SidebarMenuButton>
                ) : (
                  <SidebarMenuButton asChild tooltip={t('back_to_desk') || 'Back to Desk'} className="h-full flex-1 py-0">
                    <Link to="/desk" onClick={handleMobileNavigation} className="flex items-center w-full h-full !px-3 !py-1">
                      <ArrowLeft className="!h-4 !w-4 text-secondary-foreground" />
                      <span className="text-primary">{t('back_to_desk') || 'Back to Desk'}</span>
                    </Link>
                  </SidebarMenuButton>
                )}
                <SidebarMenuButton
                  tooltip={t('toggle_sidebar')}
                  className="h-full !justify-center !text-center !p-1"
                  onClick={toggleSidebar}
                  aria-label={t('toggle_sidebar')}
                >
                  <PanelLeftClose className="!h-6 !w-6 text-primary" />
                  <span className="sr-only">{t('toggle_sidebar')}</span>
                </SidebarMenuButton>
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <div className="relative flex min-h-0 flex-1 flex-col">
      <SidebarContent
        ref={(node) => {
          sidebarContentRef.current = node;
          setSidebarContent(node);
        }}
        className={cn(
          '[&_[data-active=true]]:!bg-[rgba(94,204,128,0.16)] [&_[data-active=true]]:!text-primary [&_[data-active=true]]:!font-medium [&_[data-active=true]]:hover:!bg-[rgba(94,204,128,0.16)] [&_[data-active=true]]:hover:!text-primary [&_[data-active=true]_.text-primary]:!font-medium [&_[data-active=true]_svg]:!text-[var(--scribane-green)]',
          isCollapsed && needsContentScroll && '!overflow-y-auto',
        )}
      >
        {project && canViewProject && (
          <ProjectExplorer
            project={project}
            projectDocuments={projectDocuments}
            setProjectDocuments={setProjectDocuments}
            onProjectUpdate={setProject}
          />
        )}
      </SidebarContent>
        {hasMoreContentBelow && (needsContentScroll || !isCollapsed) && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center pb-0.5">
            <ChevronDown className="h-3 w-3 animate-bounce text-muted-foreground" aria-hidden="true" />
          </div>
        )}
      </div>
      {project && canViewProject && (
        <>
          <SidebarSeparator className="mx-0" />
          <ProjectSidebarActions
            mode={sidebarActionsMode}
            isCollapsed={isCollapsed}
            projectId={params.projectId ?? ''}
            canEditProject={canEditProject}
            canManageSharing={canManageSharing}
            isPathActive={isPathActive}
            onNavigate={handleMobileNavigation}
          />
        </>
      )}
      <SidebarSeparator className="mx-0" />
      <div
        ref={(node) => {
          sidebarFooterRef.current = node;
          setSidebarFooter(node);
        }}
      >
        <UserAvatarMenu />
      </div>
    </Sidebar>
  );
};

